import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import api from "@/lib/api";
import TickerMarquee from "@/components/site/TickerMarquee";
import RaffleCard from "@/components/site/RaffleCard";
import FeaturedCarousel from "@/components/site/FeaturedCarousel";
import Reviews from "@/components/site/Reviews";
import { ArrowRight, Ticket, ShoppingBag } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1569531955323-33c6b2dca44b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwzfHx2aW50YWdlJTIwZm9vdGJhbGwlMjBzdGFkaXVtJTIwZGFya3xlbnwwfHx8fDE3ODA5NjE2NjB8MA&ixlib=rb-4.1.0&q=85";

export default function Home() {
  const loc = useLocation();
  const [raffles, setRaffles] = useState([]);
  const [jerseys, setJerseys] = useState([]);
  const [stats, setStats] = useState({ active_raffles: 0, jerseys: 0 });

  useEffect(() => {
    (async () => {
      try {
        const [r, j, s] = await Promise.all([
          api.get("/raffles", { params: { active_only: true } }),
          api.get("/jerseys"),
          api.get("/stats"),
        ]);
        setRaffles(r.data.slice(0, 3));
        setJerseys(j.data.slice(0, 4));
        setStats(s.data);
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  // Smooth-scroll if arriving with a #hash (e.g. /#reviews from nav)
  useEffect(() => {
    const hash = loc.hash?.slice(1);
    if (!hash) return;
    // wait a tick for the section to render
    const t = setTimeout(() => {
      const el = document.getElementById(hash);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 150);
    return () => clearTimeout(t);
  }, [loc.hash]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-[#332D2B]">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Vintage stadium" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0F0C0B]/40 via-[#0F0C0B]/70 to-[#0F0C0B]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-28 grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-8 fade-up">
            <div className="font-mono-retro text-xs tracking-[0.4em] text-[#C8A96A] uppercase flex items-center gap-3">
              <span className="inline-block w-2 h-2 bg-[#C8A96A] rounded-full animate-pulse" />
              Season Drop · Limited Run
            </div>
            <h1 className="font-display text-6xl md:text-7xl lg:text-8xl text-[#F2EBDD] mt-4 leading-[0.95]">
              EVERY SHIRT.<br />
              <span className="text-[#C8A96A]">£30 FLAT.</span>
            </h1>
            <p className="font-body text-[#F2EBDD]/85 mt-6 max-w-xl text-base md:text-lg leading-relaxed">
              World Cup 26 nation kits. 26/27 club shirts. Hand-picked, club-house priced — no markup, no nonsense.
              Add your name &amp; number, choose Fan or Player fit, all included.
            </p>
            <div className="flex flex-wrap items-center gap-3 mt-6 font-mono-retro text-[10px] tracking-[0.3em] text-[#A8A39D] uppercase">
              <span className="px-3 py-1.5 border border-[#332D2B] bg-[#1C1816]/60">Free name &amp; number</span>
              <span className="px-3 py-1.5 border border-[#332D2B] bg-[#1C1816]/60">Fan or Player fit · free</span>
              <span className="px-3 py-1.5 border border-[#332D2B] bg-[#1C1816]/60">Worldwide shipping</span>
            </div>
            <div className="flex flex-wrap items-center gap-4 mt-8">
              <Link to="/shop" data-testid="hero-cta-shop" className="btn-primary">
                Shop All £30 Kits <ArrowRight size={16} />
              </Link>
              <Link to="/raffles" data-testid="hero-cta-raffles" className="btn-secondary">
                <Ticket size={14} /> Enter a Raffle
              </Link>
            </div>
          </div>
          <div className="md:col-span-4 fade-up">
            <div className="border border-[#332D2B] bg-[#1C1816]/80 backdrop-blur-md p-6">
              <div className="font-mono-retro text-[10px] tracking-[0.3em] text-[#756F6B] uppercase">Today&apos;s Card</div>
              <div className="mt-3 grid grid-cols-2 gap-4">
                <div>
                  <div className="font-display text-5xl text-[#C8A96A]">{stats.active_raffles}</div>
                  <div className="font-mono-retro text-[10px] tracking-[0.25em] text-[#A8A39D] uppercase mt-1">Active Raffles</div>
                </div>
                <div>
                  <div className="font-display text-5xl text-[#C8A96A]">{stats.jerseys}</div>
                  <div className="font-mono-retro text-[10px] tracking-[0.25em] text-[#A8A39D] uppercase mt-1">Jerseys In Stock</div>
                </div>
              </div>
              <div className="perforated my-5" />
              <div className="font-mono-retro text-[11px] tracking-[0.2em] text-[#A8A39D] uppercase">
                Kick-Off · 19:45 · Floodlights On
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TICKER */}
      <TickerMarquee />

      {/* JERSEY SECTION */}
      <section className="bg-[#1C1816] border-y border-[#332D2B]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="font-mono-retro text-xs tracking-[0.4em] text-[#C8A96A] uppercase flex items-center gap-2">
                <ShoppingBag size={14} /> The Cabinet
              </div>
              <h2 className="font-display text-4xl md:text-5xl text-[#F2EBDD] mt-2">Classic Jerseys</h2>
            </div>
            <Link to="/shop" data-testid="see-all-jerseys" className="font-mono-retro text-xs tracking-[0.25em] text-[#C8A96A] uppercase hover:text-[#E6C875]">
              Visit Shop →
            </Link>
          </div>
          {jerseys.length === 0 ? (
            <div className="border border-dashed border-[#332D2B] p-16 text-center bg-[#0F0C0B]">
              <div className="font-display text-3xl text-[#F2EBDD]">Cabinet Is Empty</div>
              <p className="text-[#A8A39D] mt-3">The first match shirts are being pressed. Watch this space.</p>
            </div>
          ) : (
            <FeaturedCarousel jerseys={jerseys} pickCount={8} />
          )}
        </div>
      </section>

      {/* RAFFLES SECTION */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="font-mono-retro text-xs tracking-[0.4em] text-[#C8A96A] uppercase flex items-center gap-2">
              <Ticket size={14} /> Now Playing
            </div>
            <h2 className="font-display text-4xl md:text-5xl text-[#F2EBDD] mt-2">Live Raffles</h2>
          </div>
          <Link to="/raffles" data-testid="see-all-raffles" className="font-mono-retro text-xs tracking-[0.25em] text-[#C8A96A] uppercase hover:text-[#E6C875]">
            See All →
          </Link>
        </div>
        {raffles.length === 0 ? (
          <div className="border border-dashed border-[#332D2B] p-16 text-center">
            <div className="font-display text-3xl text-[#F2EBDD]">The Tunnel Is Empty</div>
            <p className="text-[#A8A39D] mt-3">No raffles on the card right now. New fixtures coming soon.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {raffles.map((r) => <RaffleCard key={r.id} raffle={r} />)}
          </div>
        )}
      </section>

      {/* REVIEWS */}
      <Reviews />

      <TickerMarquee variant="gold" items={["FAIR PLAY", "MATCHDAY CLASSICS", "ORIGINAL FITS", "TICKETS BY STRIPE", "SECURE CHECKOUT", "WORLDWIDE SHIPPING"]} />
    </div>
  );
}
